"use client";

import { PageHeader } from "@/components/page-header";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useMemo, useState } from "react";

type GeneralFeedbackItem = {
  id: string;
  feedback: string;
  created_at: string;
  target_type: string;
  created_by: {
    user_id: string;
    name: string | null;
  };
};

type AdminFeedbackListResponse = {
  items?: GeneralFeedbackItem[];
  error?: string;
};

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "–"
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(date);
}

export function GeneralFeedbackPanel({ onBack }: { onBack: () => void }) {
  const [feedback, setFeedback] = useState<GeneralFeedbackItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<GeneralFeedbackItem | null>(null);

  const loadFeedback = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: invokeError } = await supabase.functions.invoke(
      "get-admin-feedback-list",
      { body: { include_completed: true, general_only: true } }
    );

    setLoading(false);

    if (invokeError) {
      console.error("[GeneralFeedbackPanel] load feedback:", invokeError);
      setError(invokeError.message || "Failed to load feedback.");
      return;
    }

    const response = data as AdminFeedbackListResponse | null;
    if (!response?.items) {
      setError(response?.error ?? "Failed to load feedback.");
      return;
    }

    setFeedback(response.items.filter((item) => item.target_type === "general"));
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void loadFeedback();
  }, [loadFeedback]);

  const columns = useMemo<DataTableColumn<GeneralFeedbackItem>[]>(
    () => [
      {
        header: "Submitted",
        width: "w-[180px]",
        render: (item) => formatDate(item.created_at),
        sortKey: "created_at",
        sortValue: (item) => item.created_at,
      },
      {
        header: "User",
        width: "w-[200px]",
        render: (item) => item.created_by.name || item.created_by.user_id,
      },
      {
        header: "Feedback",
        render: (item) => (
          <p className="line-clamp-3 whitespace-pre-wrap">{item.feedback}</p>
        ),
      },
    ],
    []
  );

  return (
    <div
      className={cn(
        "mx-auto flex h-full w-full flex-col overflow-hidden",
        selected ? "max-w-7xl" : "max-w-3xl"
      )}
    >
      <PageHeader
        title="General Feedback"
        description="Read general feedback submitted by users."
        onBack={onBack}
        showBack
      />
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-5 sm:px-6">
        <div className={selected ? "grid items-start gap-6 sm:grid-cols-2" : "w-full"}>
          {error ? (
            <p className="text-sm text-destructive">{error}</p>
          ) : (
            <DataTable
              columns={columns}
              data={feedback}
              getRowKey={(item) => item.id}
              onRowClick={setSelected}
              loading={loading}
              onRefresh={() => void loadFeedback()}
              searchFilter={(item, query) =>
                item.feedback.toLowerCase().includes(query) ||
                (item.created_by.name ?? "").toLowerCase().includes(query) ||
                item.created_by.user_id.toLowerCase().includes(query)
              }
              searchPlaceholder="Search feedback…"
              emptyMessage="No general feedback found."
              itemLabel="entry"
              itemLabelPlural="entries"
              wrapCells
            />
          )}
          {selected && (
            <section
              className="min-w-0 space-y-4 rounded-lg border border-border bg-card p-4"
              aria-labelledby="feedback-detail-heading"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0">
                  <h2
                    id="feedback-detail-heading"
                    className="text-lg font-semibold text-foreground"
                  >
                    {selected.created_by.name || selected.created_by.user_id}
                  </h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {formatDate(selected.created_at)}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelected(null)}
                >
                  Close
                </Button>
              </div>
              <p className="whitespace-pre-wrap text-sm">{selected.feedback}</p>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
